import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { ContentProvider, RunHistoryEntry } from '@/types/knowledge';
import { useEffect, useState } from 'react';

interface RunHistorySheetProps {
    isOpen: boolean;
    onClose: () => void;
    provider: ContentProvider;
}

// Mock data for run history
const mockRunHistory: Record<string, RunHistoryEntry[]> = {
    '1': [
        {
            id: '1',
            date: '2024-01-20T10:30:00Z',
            status: 'Success',
            message: 'Content sync completed successfully',
        },
        {
            id: '2',
            date: '2024-01-19T10:30:00Z',
            status: 'Success',
            message: 'Content sync completed successfully',
        },
        {
            id: '3',
            date: '2024-01-18T10:30:00Z',
            status: 'Failure',
            message: 'Connection timeout. Please check your credentials.',
        },
    ],
    '2': [
        {
            id: '4',
            date: '2024-01-19T14:20:00Z',
            status: 'Failure',
            message: 'Connection timeout. Please check your credentials.',
        },
        {
            id: '5',
            date: '2024-01-18T14:20:00Z',
            status: 'Success',
            message: 'Content sync completed successfully',
        },
    ],
    '3': [
        {
            id: '6',
            date: '2024-01-21T09:15:00Z',
            status: 'Success',
            message: 'Content sync completed successfully',
        },
        {
            id: '7',
            date: '2024-01-20T09:15:00Z',
            status: 'Success',
            message: 'Content sync completed successfully',
        },
    ],
};

export const RunHistorySheet = ({ isOpen, onClose, provider }: RunHistorySheetProps) => {
    const [history, setHistory] = useState<RunHistoryEntry[]>([]);

    useEffect(() => {
        // Load mock history for the provider
        setHistory(mockRunHistory[provider.id] || []);
    }, [provider.id]);

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent style={{ backgroundColor: "hsl(var(--background))", width: '500px' }}>
                <SheetHeader>
                    <SheetTitle className="text-foreground">
                        Run History - {provider.name}
                    </SheetTitle>
                    <SheetDescription className="text-muted-foreground">
                        View the execution history for this content provider
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="text-primary">Date</TableHead>
                                <TableHead className="text-primary">Status</TableHead>
                                <TableHead className="text-primary">Message</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {history.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-center py-8">
                                        <p className="text-muted-foreground">No run history available</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                history.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell className="text-foreground">
                                            {new Date(entry.date).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <span
                                                className={`px-2 py-1 inline-flex text-xs font-bold rounded-full ${entry.status === 'Success'
                                                    ? 'text-secondary bg-secondary/10'
                                                    : 'text-destructive bg-destructive/10'
                                                    }`}
                                            >
                                                {entry.status}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {entry.message || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </SheetContent>
        </Sheet>
    );
};
